import React, { useState, Fragment } from 'react';
import { Dialog, Transition, Listbox } from '@headlessui/react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  ChevronUpDownIcon,
  ArrowRightCircleIcon,
} from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import {
  formatCurrency,
  formatDate,
  formatTransactionType,
  getValueColor,
} from '../../utils/formatters';
import TransactionModal from './TransactionModal';
import MoveTransactionModal from './MoveTransactionModal';

const Transactions = () => {
  const { transactions, goals, portfolios, deleteTransaction, settings } = useData();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState({ id: 'all', name: 'All Types' });
  const [goalFilter, setGoalFilter] = useState({ id: 'all', name: 'All Goals' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [isMoveModalVisible, setIsMoveModalVisible] = useState(false);
  const [movingTransaction, setMovingTransaction] = useState(null);

  const typeOptions = [
    { id: 'all', name: 'All Types' },
    { id: 'contribution', name: 'Contribution' },
    { id: 'withdrawal', name: 'Withdrawal' },
    { id: 'dividend', name: 'Dividend' },
    { id: 'fee', name: 'Fee' },
    { id: 'manual', name: 'Manual Adjustment' },
  ];

  const goalOptions = [
    { id: 'all', name: 'All Goals' },
    { id: 'unallocated', name: 'Unallocated' },
    ...goals.map(g => ({ id: g.id, name: `${g.emoji} ${g.name}` })),
  ];

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setIsModalVisible(true);
  };

  const handleDelete = (transactionId) => {
    deleteTransaction(transactionId);
    setDeleteDialogOpen(false);
    setTransactionToDelete(null);
  };

  const handleModalClose = () => {
    setEditingTransaction(null);
    setIsModalVisible(false);
  };

  const openDeleteDialog = (transaction) => {
    setTransactionToDelete(transaction);
    setDeleteDialogOpen(true);
  };

  const handleMove = (transaction) => {
    setMovingTransaction(transaction);
    setIsMoveModalVisible(true);
  };

  const handleMoveModalClose = () => {
    setMovingTransaction(null);
    setIsMoveModalVisible(false);
  };

  // Filter transactions
  const filteredTransactions = transactions.filter((txn) => {
    const matchesSearch =
      !searchText ||
      txn.description?.toLowerCase().includes(searchText.toLowerCase()) ||
      formatTransactionType(txn.type).toLowerCase().includes(searchText.toLowerCase());

    const matchesType = typeFilter.id === 'all' || txn.type === typeFilter.id;

    const matchesGoal = goalFilter.id === 'all' ||
      (goalFilter.id === 'unallocated' && !txn.goalId) ||
      txn.goalId === goalFilter.id;

    return matchesSearch && matchesType && matchesGoal;
  });

  const getTypeColor = (type) => {
    const colors = {
      contribution: '#10b981',
      withdrawal: '#ef4444',
      dividend: '#2a4d6e',
      fee: '#b8754e',
      manual: '#5987b6',
    };
    return colors[type] || '#6b7280';
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center mb-6 flex-shrink-0">
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight m-0">Transactions</h1>
        <button
          onClick={() => setIsModalVisible(true)}
          className="flex items-center gap-2 px-5 py-2.5  text-white rounded-lg font-medium  transition-colors shadow-sm"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
        >
          <PlusIcon className="w-5 h-5" />
          <span>Add Transaction</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6 flex-shrink-0">
        {/* Search */}
        <div className="flex-1 min-w-[250px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Type Filter */}
        <div className="w-48">
          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <Listbox value={typeFilter} onChange={setTypeFilter}>
            <div className="relative">
              <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <span className="block truncate">{typeFilter.name}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                </span>
              </Listbox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {typeOptions.map((option) => (
                    <Listbox.Option
                      key={option.id}
                      value={option}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                          active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {option.name}
                          </span>
                          {selected && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                              <CheckIcon className="h-5 w-5" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
        </div>

        {/* Goal Filter */}
        <div className="w-56">
          <label className="block text-sm font-medium text-gray-700 mb-1">Goal</label>
          <Listbox value={goalFilter} onChange={setGoalFilter}>
            <div className="relative">
              <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <span className="block truncate">{goalFilter.name}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                </span>
              </Listbox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {goalOptions.map((option) => (
                    <Listbox.Option
                      key={option.id}
                      value={option}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                          active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {option.name}
                          </span>
                          {selected && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                              <CheckIcon className="h-5 w-5" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden flex-1 flex flex-col min-h-0">
        <div className="overflow-x-auto overflow-y-auto flex-1">
          <table className="w-full border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-1 text-left text-xs font-semibold text-gray-600 uppercase bg-gray-50">Date</th>
                <th className="px-4 py-1 text-left text-xs font-semibold text-gray-600 uppercase bg-gray-50">Type</th>
                <th className="px-4 py-1 text-left text-xs font-semibold text-gray-600 uppercase bg-gray-50">Account</th>
                <th className="px-4 py-1 text-right text-xs font-semibold text-gray-600 uppercase bg-gray-50">Amount</th>
                <th className="px-4 py-1 text-left text-xs font-semibold text-gray-600 uppercase bg-gray-50">Goal</th>
                <th className="px-4 py-1 text-left text-xs font-semibold text-gray-600 uppercase bg-gray-50">Description</th>
                <th className="px-4 py-1 text-center text-xs font-semibold text-gray-600 uppercase bg-gray-50">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    No transactions found
                  </td>
                </tr>
              ) : (
                filteredTransactions
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .slice(0, 20)
                  .map((txn) => {
                    const portfolio = portfolios.find((p) => p.id === txn.portfolioId);
                    const goal = goals.find((g) => g.id === txn.goalId);
                    // Use portfolio currency, fallback to settings currency
                    const txnCurrency = portfolio?.currency || settings.currency;
                    // Calculate signed value for display
                    let value;
                    if (txn.type === 'contribution' || txn.type === 'dividend' || txn.type === 'manual') {
                      value = txn.amount;
                    } else {
                      // Withdrawals and fees are negative
                      value = -txn.amount;
                    }

                    return (
                      <tr key={txn.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-1 text-sm text-gray-900">
                          {formatDate(txn.date)}
                        </td>
                        <td className="px-4 py-1 text-sm">
                          <span
                            className="inline-block px-3 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: getTypeColor(txn.type) + '20',
                              color: getTypeColor(txn.type),
                            }}
                          >
                            {formatTransactionType(txn.type)}
                          </span>
                        </td>
                        <td className="px-4 py-1 text-xs text-gray-600">
                          {portfolio ? portfolio.name : 'Unknown'}
                        </td>
                        <td className="px-4 py-1 text-sm font-semibold text-right" style={{ color: getValueColor(value) }}>
                          {formatCurrency(value, txnCurrency)}
                        </td>
                        <td className="px-4 py-1 text-sm">
                          {goal ? (
                            <span
                              className="inline-block px-3 py-1 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: goal.color + '20',
                                color: goal.color,
                              }}
                            >
                              {goal.emoji} {goal.name}
                            </span>
                          ) : (
                            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                              Unallocated
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-1 text-sm text-gray-900 max-w-xs truncate">
                          {txn.description}
                        </td>
                        <td className="px-4 py-1">
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={() => handleEdit(txn)}
                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                              title="Edit"
                            >
                              <PencilIcon className="w-4 h-4 text-gray-600" />
                            </button>
                            <button
                              onClick={() => handleMove(txn)}
                              className="p-1 hover:bg-blue-100 rounded transition-colors"
                              title="Move to another account"
                            >
                              <ArrowRightCircleIcon className="w-4 h-4 text-blue-600" />
                            </button>
                            <button
                              onClick={() => openDeleteDialog(txn)}
                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                              title="Delete"
                            >
                              <TrashIcon className="w-4 h-4 text-gray-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transaction Modal */}
      {isModalVisible && (
        <TransactionModal
          visible={isModalVisible}
          onCancel={handleModalClose}
          transaction={editingTransaction}
        />
      )}

      {/* Move Transaction Modal */}
      <MoveTransactionModal
        visible={isMoveModalVisible}
        onCancel={handleMoveModalClose}
        transaction={movingTransaction}
      />

      {/* Delete Dialog */}
      <Transition appear show={deleteDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDeleteDialogOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                      Delete Transaction
                    </Dialog.Title>
                  </div>

                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete this transaction? This action cannot be undone.
                    </p>
                  </div>

                  <div className="mt-6 flex gap-3 justify-end">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        setDeleteDialogOpen(false);
                        setTransactionToDelete(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                      onClick={() => transactionToDelete && handleDelete(transactionToDelete.id)}
                    >
                      Delete
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default Transactions;
